import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CircleNotch, PencilSimple, Trash, Check, X, ArrowsMerge } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { IconPicker } from "./LocationGroups";
import { LocationIcon, useLocationIcons } from "../lib/locations";
import { listLocations, renameLocation, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function Row({ loc, all, admin, map, onDone }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(loc.name);
  const [busy, setBusy] = useState(false);
  const target = all.find((l) => l.name.toLowerCase() === val.trim().toLowerCase() && l.name !== loc.name);
  const run = async (to, msg) => {
    setBusy(true);
    try { const r = await renameLocation(loc.name, to); toast.success(`${msg} · ${r.updated} plant${r.updated !== 1 ? "s" : ""} updated`); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); setEdit(false); }
  };
  const save = () => {
    const to = target ? target.name : val.trim();
    if (!to || to === loc.name) return setEdit(false);
    if (target && !window.confirm(`Merge "${loc.name}" into "${target.name}"? All ${loc.count} plants move there.`)) return;
    run(to, target ? `Merged into ${target.name}` : `Renamed to ${to}`);
  };
  const remove = () => { if (window.confirm(`Delete "${loc.name}"? ${loc.count} plant${loc.count !== 1 ? "s" : ""} will have no location.`)) run(null, "Location deleted"); };
  return (
    <li className="py-2.5 flex items-center gap-3" data-testid={`manage-loc-${loc.name}`}>
      <IconPicker name={loc.name}>
        <button type="button" aria-label={`Change icon for ${loc.name}`} className="h-10 w-10 rounded-2xl bg-primary/10 text-primary inline-flex items-center justify-center shrink-0 hover:bg-primary/15">
          <LocationIcon name={loc.name} map={map} className="h-5 w-5" />
        </button>
      </IconPicker>
      {edit ? (
        <form data-inline-edit onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); setEdit(false); setVal(loc.name); } }} onSubmit={(e) => { e.preventDefault(); save(); }} className="flex-1 min-w-0 flex items-center gap-1.5">
          <input autoFocus value={val} onChange={(e) => setVal(e.target.value.slice(0, 80))} list="gh-locations"
            className="flex-1 min-w-0 h-9 px-3 rounded-full bg-muted/70 text-sm outline-none focus:ring-2 focus:ring-primary/40" data-testid={`manage-loc-input-${loc.name}`} />
          <button type="submit" disabled={busy} aria-label={target ? "Merge" : "Save"} className="h-9 w-9 rounded-full bg-foreground text-background inline-flex items-center justify-center shrink-0" data-testid={`manage-loc-save-${loc.name}`}>
            {busy ? <CircleNotch className="h-4 w-4 animate-spin" /> : target ? <ArrowsMerge className="h-4 w-4" /> : <Check weight="bold" className="h-4 w-4" />}
          </button>
          <button type="button" onClick={() => { setEdit(false); setVal(loc.name); }} aria-label="Cancel" className="h-9 w-9 rounded-full hover:bg-muted inline-flex items-center justify-center shrink-0"><X className="h-4 w-4" /></button>
        </form>
      ) : (
        <>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{loc.name}</p>
            <p className="text-[11px] text-muted-foreground">{loc.count} plant{loc.count !== 1 ? "s" : ""}</p>
          </div>
          {admin && (
            <>
              <button type="button" onClick={() => setEdit(true)} aria-label={`Rename or merge ${loc.name}`} data-testid={`manage-loc-edit-${loc.name}`} className="h-9 w-9 rounded-full hover:bg-muted inline-flex items-center justify-center text-muted-foreground"><PencilSimple className="h-4 w-4" /></button>
              <button type="button" onClick={remove} disabled={busy} aria-label={`Delete ${loc.name}`} data-testid={`manage-loc-delete-${loc.name}`} className="h-9 w-9 rounded-full hover:bg-destructive/10 inline-flex items-center justify-center text-muted-foreground hover:text-destructive"><Trash className="h-4 w-4" /></button>
            </>
          )}
        </>
      )}
      {edit && target && <span className="sr-only">Will merge</span>}
    </li>
  );
}

export default function LocationsDialog({ open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const [map] = useLocationIcons();
  const [locs, setLocs] = useState(null);
  const load = () => listLocations().then(setLocs).catch(() => setLocs([]));
  useEffect(() => { if (open) load(); }, [open]);
  const admin = user?.role === "admin";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-[calc(100vw-1.5rem)] max-h-[88dvh] overflow-y-auto rounded-3xl border-0" data-testid="locations-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="text-xl font-semibold tracking-tight">Locations</DialogTitle>
          <DialogDescription>{admin ? "Tap an icon to change it. Rename to an existing name to merge. Moves are logged on each plant's timeline." : "Tap an icon to change it. Only admins can rename, merge or delete."}</DialogDescription>
        </DialogHeader>
        <datalist id="gh-locations">{(locs || []).map((l) => <option key={l.name} value={l.name} />)}</datalist>
        {!locs ? <div className="py-8 flex justify-center"><CircleNotch className="h-5 w-5 animate-spin" /></div>
          : locs.length ? (
            <ul className="divide-y divide-border/60" data-testid="locations-list">
              {locs.map((l) => <Row key={l.name} loc={l} all={locs} admin={admin} map={map} onDone={() => { load(); onChanged?.(); }} />)}
            </ul>
          ) : <p className="py-6 text-sm text-muted-foreground text-center">No locations yet. Set one from any plant's page.</p>}
      </DialogContent>
    </Dialog>
  );
}
